import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Image, Spinner } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { Formik, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { UserRole } from '../../types';
import { FaUser, FaCamera, FaPhone, FaEnvelope, FaMapMarkerAlt, FaIdCard, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import './Profile.css';

interface ProfileFormValues {
  name: string;
  phoneNumber: string;
  address?: string;
  bio?: string;
  specialization?: string;
}

const Profile: React.FC = () => {
  const { currentUser, userData, setUserData, photoURL, setPhotoURL } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialValues: ProfileFormValues = {
    name: userData?.name || '',
    phoneNumber: userData?.phoneNumber || '',
    address: userData?.address || '',
    bio: userData?.bio || '',
    specialization: userData?.specialization || '',
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('الاسم مطلوب'),
    phoneNumber: Yup.string().required('رقم الهاتف مطلوب'),
    address: Yup.string(),
    bio: Yup.string(),
    specialization: Yup.string(),
  });

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentUser) return;

    try {
      setUploadingPhoto(true);
      setError(null);

      // التحقق من نوع الملف
      if (!file.type.startsWith('image/')) {
        setError('يرجى اختيار ملف صورة صالح');
        setUploadingPhoto(false);
        return;
      }

      // التحقق من حجم الملف (أقل من 5 ميجابايت)
      if (file.size > 5 * 1024 * 1024) {
        setError('حجم الصورة كبير جدًا. يرجى اختيار صورة أقل من 5 ميجابايت');
        setUploadingPhoto(false);
        return;
      }

      const storageRef = ref(storage, `profile_photos/${currentUser.uid}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // تحديث URL الصورة في Firestore - تحديث كلا الحقلين photoURL و profilePicture
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { 
        photoURL: downloadURL,
        profilePicture: downloadURL,
        updatedAt: Date.now()
      });

      // تحديث حالة الصورة في التطبيق
      if (setPhotoURL) {
        setPhotoURL(downloadURL);
      }
      
      // تحديث بيانات المستخدم المحلية
      if (userData && setUserData) {
        setUserData({
          ...userData,
          photoURL: downloadURL,
          profilePicture: downloadURL
        });
      }
      
      setSuccess('تم تحديث صورة الملف الشخصي بنجاح');

    } catch (error) {
      console.error('Error uploading photo:', error);
      setError('حدث خطأ أثناء تحميل الصورة. يرجى المحاولة مرة أخرى');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (values: ProfileFormValues, { setSubmitting }: FormikHelpers<ProfileFormValues>) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);
      
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        name: values.name,
        phoneNumber: values.phoneNumber,
        address: values.address || '',
        bio: values.bio || '',
        specialization: values.specialization || '',
        updatedAt: new Date().toISOString(),
      });

      // تحديث بيانات المستخدم في حالة التطبيق
      if (userData) {
        setUserData({
          ...userData,
          name: values.name,
          phoneNumber: values.phoneNumber,
          address: values.address || '',
          bio: values.bio || '',
          specialization: values.specialization || '',
        });
      }

      setSuccess('تم تحديث البيانات الشخصية بنجاح');
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('حدث خطأ أثناء تحديث البيانات. يرجى المحاولة مرة أخرى');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  if (!currentUser || !userData) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">جاري تحميل البيانات...</p>
      </Container>
    );
  }

  return (
    <Container className="profile-page">
      <div className="profile-header">
        <h1 className="profile-title">الملف الشخصي</h1>
      </div>
      
      {error && (
        <Alert variant="danger" className="profile-alert" onClose={() => setError(null)} dismissible>
          <FaExclamationTriangle className="profile-alert-icon" />
          <div>{error}</div>
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" className="profile-alert" onClose={() => setSuccess(null)} dismissible>
          <FaCheck className="profile-alert-icon" />
          <div>{success}</div>
        </Alert>
      )}
      
      <Row>
        <Col lg={4} md={5} className="mb-4">
          <Card className="profile-card">
            <Card.Body className="text-center p-4">
              <div className="profile-image-container">
                {photoURL ? (
                  <Image 
                    src={photoURL} 
                    alt="صورة المستخدم" 
                    roundedCircle 
                    className="profile-image" 
                  />
                ) : (
                  <div className="profile-image-placeholder">
                    <FaUser size={60} className="text-primary" />
                  </div>
                )}
                <Button 
                  variant="primary" 
                  className="profile-upload-btn"
                  onClick={triggerFileInput}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaCamera />
                  )}
                </Button>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="d-none" 
                  accept="image/*" 
                  onChange={handlePhotoUpload} 
                />
              </div>
              
              <h3 className="profile-name">{userData.name || 'المستخدم'}</h3>
              <p className="profile-role">{userData.specialization || 'مستخدم منصة السامي التعليمية'}</p>
              
              <div className="d-flex flex-column align-items-start text-start">
                <div className="profile-info-item">
                  <div className="profile-info-icon">
                    <FaPhone />
                  </div>
                  <div className="profile-info-content">
                    <div className="profile-info-label">رقم الهاتف</div>
                    <div className="profile-info-value">{userData.phoneNumber || 'غير محدد'}</div>
                  </div>
                </div>
                
                <div className="profile-info-item">
                  <div className="profile-info-icon">
                    <FaEnvelope />
                  </div>
                  <div className="profile-info-content">
                    <div className="profile-info-label">البريد الإلكتروني</div>
                    <div className="profile-info-value">{currentUser.email || 'غير محدد'}</div>
                  </div>
                </div>
                
                {userData.address && (
                  <div className="profile-info-item">
                    <div className="profile-info-icon">
                      <FaMapMarkerAlt />
                    </div>
                    <div className="profile-info-content">
                      <div className="profile-info-label">العنوان</div>
                      <div className="profile-info-value">{userData.address}</div>
                    </div>
                  </div>
                )}
                
                <div className="profile-info-item">
                  <div className="profile-info-icon">
                    <FaIdCard />
                  </div>
                  <div className="profile-info-content">
                    <div className="profile-info-label">نوع الحساب</div>
                    <div className="profile-info-value">
                      {userData.role === UserRole.ADMIN && 'مسؤول'}
                      {userData.role === UserRole.TEACHER && 'معلم'}
                      {userData.role === UserRole.STUDENT && 'طالب'}
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={8} md={7}>
          <Card className="profile-edit-card">
            <Card.Header className="profile-edit-header">
              تعديل البيانات الشخصية
            </Card.Header>
            <Card.Body className="profile-edit-body">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ handleSubmit, isSubmitting }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="form-group" controlId="name">
                          <Form.Label className="fw-bold">الاسم</Form.Label>
                          <div className="form-control-icon">
                            <FaUser className="icon" />
                            <Field
                              as={Form.Control}
                              type="text"
                              name="name"
                              placeholder="أدخل اسمك"
                            />
                          </div>
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="text-danger mt-1"
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="form-group" controlId="phoneNumber">
                          <Form.Label className="fw-bold">رقم الهاتف</Form.Label>
                          <div className="form-control-icon">
                            <FaPhone className="icon" />
                            <Field
                              as={Form.Control}
                              type="text"
                              name="phoneNumber"
                              placeholder="أدخل رقم الهاتف"
                            />
                          </div>
                          <ErrorMessage
                            name="phoneNumber"
                            component="div"
                            className="text-danger mt-1"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="form-group" controlId="address">
                      <Form.Label className="fw-bold">العنوان</Form.Label>
                      <div className="form-control-icon">
                        <FaMapMarkerAlt className="icon" />
                        <Field
                          as={Form.Control}
                          type="text"
                          name="address"
                          placeholder="أدخل عنوانك (اختياري)"
                        />
                      </div>
                      <ErrorMessage
                        name="address"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </Form.Group>
                    
                    <Form.Group className="form-group" controlId="specialization">
                      <Form.Label className="fw-bold">التخصص</Form.Label>
                      <Field
                        as={Form.Control}
                        type="text"
                        name="specialization"
                        placeholder="أدخل تخصصك (اختياري)"
                      />
                      <ErrorMessage
                        name="specialization"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </Form.Group>
                    
                    <Form.Group className="form-group" controlId="bio">
                      <Form.Label className="fw-bold">نبذة عنك</Form.Label>
                      <Field
                        as="textarea"
                        rows={4}
                        name="bio"
                        className="form-control"
                        placeholder="اكتب نبذة قصيرة عنك (اختياري)"
                      />
                      <ErrorMessage
                        name="bio"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </Form.Group>
                    
                    <div className="d-grid">
                      <Button
                        variant="primary"
                        type="submit"
                        className="profile-save-btn"
                        disabled={loading || isSubmitting}
                      >
                        {loading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;
